import { FC, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { DetailedCarrier } from 'modules/domain/logisticsKR/types'
import { RightPanelComments } from 'views/components/Comments/styled'
import { useParams } from 'react-router-dom'
import { endpoints } from 'modules/endpoints'
import { AntCollapse, Panel } from '@agro-club/agroclub-shared'
import { defaultListRequestParams } from 'views/components/TableFilters/data'
import { CarrierDocuments } from './CarrierDocuments/CarrierDocuments'
import { CarrierFiles } from './CarrierFiles/CarrierFiles'
import { CarrierInfo } from './CarrierInfo/CarrierInfo'
import AComments from 'views/components/Comments/AComments'
import { Employees } from './Employees/Employees'
import AntCollapseHeader from 'views/components/AntCollapseHeader/AntCollapseHeader'
import { SummaryComments } from 'views/components/AntCollapseHeader/SummaryComments'
import { SummaryCount } from 'views/components/AntCollapseHeader/SummaryCount'
import { SummaryThereIsNo } from 'views/components/AntCollapseHeader/SummaryThereIsNo'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { DocumentFile } from 'modules/domain/types'
import { CarrierContractDocuments } from './CarrierDocuments/CarrierContractDocuments'

const CarrierAccordion: FC<{ carrier: DetailedCarrier }> = ({ carrier }) => {
  const { t } = useTranslation('logisticsKR')
  const { id } = useParams()
  const [listRequestParams] = useState({
    ...defaultListRequestParams,
    filter: { carrier: carrier.id },
    pageSize: 10000,
  })
  const [commentsSummary, setCommentsSummary] = useState<string>()
  const [hasEmployees, setHasEmployees] = useState<number>()
  const [carrierDocsSummary, setCarrierDocsSummary] = useState<number>()
  const [contractsDocsSummary, setContractsDocsSummary] = useState<number>()
  const [filesSummary, setFilesSummary] = useState<number>()

  const [progress, documents = [], documentsRefetch] = useSingleEntity<DocumentFile[]>(
    endpoints.LogisticsKR.carrierDocuments(carrier.id),
  )

  const options = [
    { value: 'comments', title: t('carriers.accordion.titles.comments') },
    { value: 'user', title: t('carriers.accordion.titles.company_info') },
    { value: 'employees', title: t('carriers.accordion.titles.employees') },
    { value: 'contract_types', title: t('carriers.accordion.titles.contract_types') },
    { value: 'carrier_documents', title: t('carriers.accordion.titles.carrier_documents') },
    { value: 'files', title: t('carriers.accordion.titles.files') },
  ]

  const components = {
    comments: (
      <RightPanelComments>
        <AComments
          url={endpoints.LogisticsKR.carrierComments(id)}
          popSummary={setCommentsSummary}
          maxHeight={400}
          id={id}
        />
      </RightPanelComments>
    ),
    user: <CarrierInfo carrier={carrier} />,
    employees: <Employees listRequestParams={listRequestParams} carrierId={carrier.id} setSummary={setHasEmployees} />,
    contract_types: (
      <CarrierContractDocuments
        carrierId={carrier.id}
        setSummary={setContractsDocsSummary}
        documentsRefetch={documentsRefetch}
        documentsProgress={progress}
        documents={documents}
      />
    ),
    carrier_documents: (
      <CarrierDocuments
        carrierId={carrier.id}
        setSummary={setCarrierDocsSummary}
        documentsRefetch={documentsRefetch}
        documentsProgress={progress}
        documents={documents}
      />
    ),
    files: <CarrierFiles setSummary={setFilesSummary} carrierId={carrier.id} />,
  }

  const summary = {
    comments: <SummaryComments summary={commentsSummary} />,
    employees: <SummaryThereIsNo hasData={hasEmployees} />,
    contract_types: <SummaryThereIsNo hasData={contractsDocsSummary} />,
    carrier_documents: <SummaryThereIsNo hasData={carrierDocsSummary} />,
    files: <SummaryCount total={filesSummary} />,
  }

  const [activeKey, setActiveKey] = useState<string | string[]>('user')

  return (
    <div>
      <AntCollapse onChange={(key) => setActiveKey(key)} defaultActiveKey="user" bordered={false} accordion>
        {options.map((option) => (
          <Panel
            forceRender
            key={option.value}
            header={
              <AntCollapseHeader title={option.title} summary={activeKey !== option.value && summary[option.value]} />
            }
          >
            {components[option.value]}
          </Panel>
        ))}
      </AntCollapse>
    </div>
  )
}

export default CarrierAccordion
